import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/lib/format";
import {
  Flame, AlertTriangle, CircleDashed, CircleCheck,
  Wallet, Calendar, Building2, Store, TrendingUp, TrendingDown, CreditCard,
  Clock, PackageSearch,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import SalesAnalyticsDashboard from "@/pages/dashboard/SalesAnalyticsDashboard";

const PRIORITY_META = {
  urgent: { label: "Urgent", cls: "pill-urgent", icon: Flame },
  high: { label: "High", cls: "pill-high", icon: AlertTriangle },
  normal: { label: "Normal", cls: "pill-normal", icon: CircleCheck },
  low: { label: "Low", cls: "pill-low", icon: CircleDashed },
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [brands, setBrands] = useState([]);
  const [dues, setDues] = useState(null);
  const [creditByShop, setCreditByShop] = useState(null);
  const [expiringSoon, setExpiringSoon] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dashboard/brand-stats").then((r) => setBrands(r.data.items || [])).catch(() => {});
    api.get("/dashboard/dues").then((r) => setDues(r.data)).catch(() => {});
    api.get("/dashboard/customer-credits-by-shop").then((r) => setCreditByShop(r.data)).catch(() => {});
    api.get("/stock/expiring-soon?days=30").then((r) => setExpiringSoon(r.data)).catch(() => {});
  }, []);

  return (
    <div className="fade-up" data-testid="dashboard-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Overview</p>
        <h1 className="font-heading text-3xl tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">
          A quick snapshot of your inventory and restock pipeline.
        </p>
      </div>

      <div className="mt-2">
        <h2 className="font-heading text-lg tracking-tight mb-4">Requirement by priority</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["urgent", "high", "normal", "low"].map((p) => {
            const M = PRIORITY_META[p];
            return (
              <Card key={p} className="p-5 flex items-center gap-4" data-testid={`priority-${p}-card`}>
                <div className={`pill ${M.cls}`}>
                  <M.icon className="w-3 h-3" />
                  {M.label}
                </div>
                <div className="ml-auto font-mono-data text-2xl font-semibold">
                  {stats?.by_priority?.[p] ?? "—"}
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Sales Analytics Dashboard - Replaces old Today Summary */}
      <div className="mt-10" data-testid="sales-analytics-section">
        <SalesAnalyticsDashboard />
      </div>

      {/* Expiring Soon Widget */}
      <div className="mt-10" data-testid="expiring-soon-section">
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="font-heading text-lg tracking-tight flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" /> Expiring Soon
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Stock batches expiring within 30 days across all shops.
            </p>
          </div>
          {expiringSoon && expiringSoon.total_count > 0 && (
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Total items</div>
              <div className="font-mono-data text-2xl font-semibold text-amber-600" data-testid="expiring-total">
                {expiringSoon.total_count}
              </div>
              {expiringSoon.expired_count > 0 && (
                <div className="text-xs text-red-600 font-medium mt-0.5" data-testid="expired-count">
                  {expiringSoon.expired_count} already expired
                </div>
              )}
            </div>
          )}
        </div>
        <Card className="overflow-hidden" data-testid="expiring-soon-card">
          {!expiringSoon || expiringSoon.total_count === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center" data-testid="expiring-empty">
              No items expiring in the next 30 days. Stock is fresh!
            </div>
          ) : (
            <>
              {/* Summary badges */}
              <div className="p-4 border-b border-[hsl(var(--border))] flex flex-wrap gap-2">
                {expiringSoon.expired_count > 0 && (
                  <span className="pill pill-urgent" data-testid="badge-expired">
                    <AlertTriangle className="w-3 h-3" />
                    {expiringSoon.expired_count} Expired
                  </span>
                )}
                {expiringSoon.critical_count > 0 && (
                  <span className="pill pill-high" data-testid="badge-critical">
                    <Flame className="w-3 h-3" />
                    {expiringSoon.critical_count} Within 7 days
                  </span>
                )}
                {expiringSoon.expiring_soon_count > 0 && (
                  <span className="pill pill-normal" data-testid="badge-expiring-soon">
                    <Clock className="w-3 h-3" />
                    {expiringSoon.expiring_soon_count} Within 30 days
                  </span>
                )}
              </div>
              {/* Item list - show max 8 items */}
              <div className="divide-y divide-[hsl(var(--border))]">
                {expiringSoon.items.slice(0, 8).map((item) => {
                  const isExpired = item.days_until_expiry < 0;
                  const isCritical = !isExpired && item.days_until_expiry <= 7;
                  return (
                    <div
                      key={item.id}
                      className="p-4 flex flex-wrap items-center gap-3 hover:bg-[hsl(var(--muted))] transition-colors"
                      data-testid={`expiring-row-${item.id}`}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                        <PackageSearch className="w-4 h-4 text-amber-600" />
                        <div>
                          <div className="font-medium text-sm">{item.product_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {item.brand_name && <span>{item.brand_name} · </span>}
                            {item.shop_name}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs font-mono-data text-muted-foreground">
                        <Calendar className="w-3.5 h-3.5" />
                        {item.expiry_date}
                      </div>
                      <span
                        className={`pill ${
                          isExpired ? "pill-urgent" : isCritical ? "pill-high" : "pill-normal"
                        }`}
                      >
                        {isExpired
                          ? `${Math.abs(item.days_until_expiry)}d overdue`
                          : item.days_until_expiry === 0
                          ? "Expires today"
                          : `in ${item.days_until_expiry}d`}
                      </span>
                      <div className="text-right min-w-[60px]">
                        <div className="font-mono-data font-semibold text-sm">
                          {item.quantity} pcs
                        </div>
                        {item.mrp && (
                          <div className="text-xs text-muted-foreground">₹{item.mrp}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {expiringSoon.total_count > 8 && (
                <div className="p-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => nav("/stock")}
                    data-testid="expiring-view-all"
                  >
                    View all {expiringSoon.total_count} expiring items →
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Supplier pending dues (PO) */}
      <div className="mt-10" data-testid="dues-section">
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="font-heading text-lg tracking-tight flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#9E6D5B]" /> Supplier pending dues
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Unpaid purchase orders due in the next 7 days (and anything overdue).
            </p>
          </div>
          {dues && dues.count > 0 && (
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Total due</div>
              <div className="font-mono-data text-2xl font-semibold text-[#9E6D5B]" data-testid="dues-total">
                ₹{Number(dues.total_due || 0).toFixed(2)}
              </div>
              {dues.overdue > 0 && (
                <div className="text-xs text-red-600 font-medium mt-0.5" data-testid="dues-overdue-count">
                  {dues.overdue} overdue
                </div>
              )}
            </div>
          )}
        </div>
        <Card className="overflow-hidden" data-testid="dues-card">
          {!dues || dues.count === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center" data-testid="dues-empty">
              Nothing due in the next 7 days. You're all caught up.
            </div>
          ) : (
            <div className="divide-y divide-[hsl(var(--border))]">
              {dues.items.map((d) => {
                const noDate = d.days_left === null || d.days_left === undefined;
                const overdue = !noDate && d.days_left < 0;
                const todayDue = !noDate && d.days_left === 0;
                return (
                  <button
                    key={d.id}
                    onClick={() => nav(`/purchase?po=${d.id}`)}
                    className="w-full text-left p-4 flex flex-wrap items-center gap-3 hover:bg-[hsl(var(--muted))] transition-colors"
                    data-testid={`dues-row-${d.id}`}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                      <Building2 className="w-4 h-4 text-[#9E6D5B]" />
                      <div>
                        <div className="font-medium text-sm">{d.supplier_name || "—"}</div>
                        <div className="text-xs text-muted-foreground font-mono-data">
                          {d.invoice_number || d.id.slice(0, 8)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-mono-data text-muted-foreground">
                      <Calendar className="w-3.5 h-3.5" />
                      {noDate ? "no due date" : formatDate(d.due_date)}
                    </div>
                    <span
                      className={`pill ${
                        overdue ? "pill-urgent" : todayDue ? "pill-high" : noDate ? "pill-low" : "pill-normal"
                      }`}
                    >
                      {overdue
                        ? `${Math.abs(d.days_left)}d overdue`
                        : todayDue
                        ? "Due today"
                        : noDate
                        ? (d.payment_mode === "credit" ? "Credit · open" : "No date")
                        : `in ${d.days_left}d`}
                    </span>
                    <div className="font-mono-data font-semibold text-sm min-w-[90px] text-right">
                      ₹{Number(d.total || 0).toFixed(2)}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Customer credit dues by shop */}
      <div className="mt-10" data-testid="customer-credits-section">
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="font-heading text-lg tracking-tight flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#9E6D5B]" /> Customer credit dues
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Outstanding balances on credit sales — broken down per shop.
            </p>
          </div>
          {creditByShop && creditByShop.total > 0 && (
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Outstanding</div>
              <div className="font-mono-data text-2xl font-semibold text-[#B4602A]" data-testid="customer-credit-total">
                ₹{Number(creditByShop.total || 0).toFixed(2)}
              </div>
            </div>
          )}
        </div>
        <Card className="overflow-hidden" data-testid="customer-credits-card">
          {!creditByShop || !creditByShop.items?.length ? (
            <div className="text-sm text-muted-foreground py-10 text-center" data-testid="customer-credits-empty">
              No outstanding customer credit. Healthy cashflow.
            </div>
          ) : (
            <div className="divide-y divide-[hsl(var(--border))]">
              {creditByShop.items.map((row) => (
                <button
                  key={row.shop_id || row.shop_name}
                  onClick={() => nav("/sales")}
                  className="w-full text-left p-4 flex items-center gap-3 hover:bg-[hsl(var(--muted))] transition-colors"
                  data-testid={`customer-credit-row-${row.shop_id || "none"}`}
                >
                  <Store className="w-4 h-4 text-[#9E6D5B]" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{row.shop_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {row.count} sale{row.count === 1 ? "" : "s"} pending
                    </div>
                  </div>
                  <div className="font-mono-data font-semibold text-base text-[#B4602A]">
                    ₹{Number(row.outstanding || 0).toFixed(2)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="mt-10" data-testid="brand-chart-section">
        <h2 className="font-heading text-lg tracking-tight mb-4">Top brands · restock activity</h2>
        <Card className="p-5">
          {brands.length === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center">
              No brand activity yet. Mark items as requirement or purchased to see trends here.
            </div>
          ) : (
            <div className="w-full" style={{ minHeight: 320, height: 320 }}>
              <ResponsiveContainer width="99%" height="100%" minWidth={0} minHeight={320}>
                <BarChart data={brands} margin={{ top: 8, right: 12, left: 0, bottom: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                  <XAxis
                    dataKey="brand"
                    stroke="#827C7A"
                    tick={{ fontSize: 11 }}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={50}
                  />
                  <YAxis stroke="#827C7A" tick={{ fontSize: 11 }} />
                  <Tooltip cursor={{ fill: "rgba(158,109,91,0.06)" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="requirement" name="Pending" fill="#9E6D5B" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="purchased" name="Purchased" fill="#CBB8AE" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
